import React from 'react';
import { Modal, Button, Group, Text, Stack, FileInput, LoadingOverlay, List } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { IconFileSpreadsheet, IconUpload } from '@tabler/icons-react';

interface ImportDialogProps {
  importing: boolean;
  opened: boolean;
  file: File | null;
  dialogTitle: string;
  confirmButtonText: string;
  onClose: () => void;
  onConfirmClicked: () => void;
  setFile: (file: File | null) => void;
  currentMaxId?: number;
}

export const ImportDialog: React.FC<ImportDialogProps> = ({
  importing,
  opened,
  file,
  dialogTitle,
  confirmButtonText,
  setFile,
  onClose,
  onConfirmClicked,
}) => {
  const { t } = useTranslation();

  const handleFileSelect = (file: File | null) => {
    if (file) {
      if (
        file.type === 'text/csv' ||
        file.name.endsWith('.csv') ||
        file.name.endsWith('.xlsx') ||
        file.name.endsWith('.xls')
      ) {
        setFile(file);
      } else {
        notifications.show({
          message: t('adminConsole.newConsole.import.invalidFormat'),
          color: 'red',
        });
        setFile(null);
      }
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={dialogTitle} size="lg">
      <LoadingOverlay visible={importing} />
      <Stack>
        {/* 导入说明 */}
        <Text size="sm" fw={500}>
          {t('adminConsole.newConsole.import.checklist.title')}
        </Text>
        <List className="list-disc" size="sm">
          <List.Item>{t('adminConsole.newConsole.import.checklist.removeExample')}</List.Item>
          <List.Item>{t('adminConsole.newConsole.import.checklist.departmentDelimiter')}</List.Item>
          <List.Item>{t('adminConsole.newConsole.import.checklist.correctPermission')}</List.Item>
        </List>

        {/* 文件上传 */}
        <FileInput
          label={t('adminConsole.newConsole.import.selectFile')}
          placeholder={t('adminConsole.newConsole.import.selectFilePlaceholder')}
          rightSection={<IconFileSpreadsheet />}
          accept=".csv,.xlsx,.xls"
          value={file}
          onChange={handleFileSelect}
        />

        {file && (
          <Text size="sm" c="dimmed">
            {t('adminConsole.newConsole.import.selectedFile')}: {file.name} ({(file.size / 1024).toFixed(2)} KB)
          </Text>
        )}

        <Group justify="flex-end">
          <Button variant="outline" onClick={onClose} disabled={importing}>
            {t('adminConsole.newConsole.import.cancel')}
          </Button>
          <Button
            onClick={onConfirmClicked}
            disabled={!file || importing}
            loading={importing}
            leftSection={<IconUpload size={16} />}
          >
            {confirmButtonText}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
